/**
 * Centralized Pricing Configuration
 * 
 * All pricing logic and plan definitions for Safermark across web app and API.
 * Stripe Price IDs are still stored in AWS Secrets Manager for security.
 */

export interface RecurringPlan {
  id: 'monthly' | 'yearly';
  name: string;
  price: number;
  currency: string;
  period: string;
  savings?: string;
  recommended?: boolean;
  description: string[];
  features: string[];
}

export interface PrepaidPlan {
  id: '3m' | '6m' | '1y' | '5y';
  name: string;
  price: number;
  currency: string;
  duration: string;
  durationInDays: number;
  savings: number;
  monthlyEquivalent: number;
  recommended?: boolean;
  description: string;
  features: string[];
}

export interface FreePlan {
  id: 'free';
  name: string;
  price: number;
  currency: string;
  description: string[];
  features: string[];
  limitations: string[];
}

export interface PricingConfig {
  currency: string;
  free: FreePlan;
  recurring: RecurringPlan[];
  prepaid: PrepaidPlan[];
}

// Standard premium features available to all paid plans
const PREMIUM_FEATURES = [
  'Everything in Free',
  'Cloud sync across devices',
  'Web access from any browser',
  'Mobile-optimized interface',
  'Automatic backup & restore',
  'Priority support'
];

const FREE_FEATURES = [
  'Unlimited local vaults',
  'AES-256 encryption',
  'Zero-knowledge security',
  'Bookmark & note organization',
  'Import/export functionality',
  'Drag & drop sorting'
];

const FREE_LIMITATIONS = [
  'Local storage only',
  'No cloud sync',
  'Single device access',
  'Community support only'
];

// Main pricing configuration
export const PRICING_CONFIG: PricingConfig = {
  currency: 'USD',
  
  // Free tier
  free: {
    id: 'free',
    name: 'Free',
    price: 0,
    currency: 'USD',
    description: [
      'Perfect for personal use on a single device'
    ],
    features: FREE_FEATURES,
    limitations: FREE_LIMITATIONS
  },
  
  // Recurring subscription plans
  recurring: [
    {
      id: 'monthly',
      name: 'Monthly',
      price: 2,
      currency: 'USD',
      period: '/month',
      description: [
        'Great for trying out premium features'
      ],
      features: PREMIUM_FEATURES
    },
    {
      id: 'yearly',
      name: 'Yearly',
      price: 13,
      currency: 'USD',
      period: '/year',
      savings: 'Save $11 - 45% Off!',
      recommended: true,
      description: [
        'Best value for regular users'
      ],
      features: PREMIUM_FEATURES
    }
  ],

  // Prepaid one-time payment plans
  prepaid: [
    {
      id: '3m',
      name: '3 Months',
      price: 5,
      currency: 'USD',
      duration: '3 months',
      durationInDays: 90,
      monthlyEquivalent: 3,
      savings: (2 * 3) - 5,
      description: 'One-time payment for 3 months of premium access',
      features: PREMIUM_FEATURES
    },
    {
      id: '6m',
      name: '6 Months',
      price: 8,
      currency: 'USD',
      duration: '6 months',
      durationInDays: 180,
      monthlyEquivalent: 6,
      savings: (2 * 6) - 8,
      description: 'One-time payment for 6 months of premium access',
      features: PREMIUM_FEATURES
    },
    {
      id: '1y',
      name: '1 Year',
      price: 13,
      currency: 'USD',
      duration: '1 year',
      durationInDays: 365,
      monthlyEquivalent: 12,
      savings: (2 * 12) - 13,
      recommended: true,
      description: 'One-time payment for 1 year of premium access',
      features: PREMIUM_FEATURES
    },
    {
      id: '5y',
      name: '5 Years',
      price: 50,
      currency: 'USD',
      duration: '5 years',
      durationInDays: 1825,
      monthlyEquivalent: 60,
      savings: (2 * 12 * 5) - 50,
      description: 'One-time payment for 5 years of premium access',
      features: PREMIUM_FEATURES
    }
  ]
};

// Helper functions for pricing calculations and lookups

export function getFreePlan(): FreePlan {
  return PRICING_CONFIG.free;
}

export function getRecurringPlan(planId: 'monthly' | 'yearly'): RecurringPlan | undefined {
  return PRICING_CONFIG.recurring.find(plan => plan.id === planId);
}

export function getPrepaidPlan(planId: '3m' | '6m' | '1y' | '5y'): PrepaidPlan | undefined {
  return PRICING_CONFIG.prepaid.find(plan => plan.id === planId);
}

export function getAllRecurringPlans(): RecurringPlan[] {
  return PRICING_CONFIG.recurring;
}

export function getAllPrepaidPlans(): PrepaidPlan[] {
  return PRICING_CONFIG.prepaid;
}

export function getAllPlans(): (FreePlan | RecurringPlan | PrepaidPlan)[] {
  return [PRICING_CONFIG.free, ...PRICING_CONFIG.recurring, ...PRICING_CONFIG.prepaid];
}

export function getRecommendedRecurringPlan(): RecurringPlan | undefined {
  return PRICING_CONFIG.recurring.find(plan => plan.recommended);
}

export function getRecommendedPrepaidPlan(): PrepaidPlan | undefined {
  return PRICING_CONFIG.prepaid.find(plan => plan.recommended);
}

// Map prepaid plan IDs to Stripe price type format
export function prepaidPlanToStripeType(planId: '3m' | '6m' | '1y' | '5y'): string {
  return `prepaid_${planId}`;
}

// Map Stripe price type back to prepaid plan ID
export function stripeToPrepaidPlan(stripeType: string): '3m' | '6m' | '1y' | '5y' | null {
  const match = stripeType.match(/^prepaid_(.+)$/);
  if (!match) return null;
  
  const planId = match[1] as '3m' | '6m' | '1y' | '5y';
  return getPrepaidPlan(planId) ? planId : null;
}

// Get duration in seconds for prepaid plans (used in API webhook processing)
export function getPrepaidDurationInSeconds(planId: '3m' | '6m' | '1y' | '5y'): number {
  const plan = getPrepaidPlan(planId);
  if (!plan) return 0;
  
  return plan.durationInDays * 24 * 60 * 60;
}

// Format price for display
export function formatPrice(price: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
}

// Calculate savings percentage for prepaid plans
export function calculateSavingsPercentage(prepaidPlan: PrepaidPlan): number {
  if (prepaidPlan.savings <= 0) return 0;
  return Math.round((prepaidPlan.savings / (prepaidPlan.monthlyEquivalent * 2)) * 100);
}

// Calculate monthly equivalent price for any plan
export function getMonthlyEquivalentPrice(plan: RecurringPlan | PrepaidPlan): number {
  if ('period' in plan) {
    // Recurring plan
    return plan.id === 'yearly' ? plan.price / 12 : plan.price;
  } else {
    // Prepaid plan
    return plan.price / (plan.durationInDays / 30.44); // Average days per month
  }
}

// Validation functions
export function isValidRecurringPlan(planId: string): planId is RecurringPlanId {
  return RECURRING_PLAN_IDS.includes(planId as RecurringPlanId);
}

export function isValidPrepaidPlan(planId: string): planId is PrepaidPlanId {
  return PREPAID_PLAN_IDS.includes(planId as PrepaidPlanId);
}

export function isValidStripePriceType(priceType: string): priceType is StripePriceType {
  return STRIPE_PRICE_TYPES.includes(priceType as StripePriceType);
}

// Export types for use in other packages
export type RecurringPlanId = 'monthly' | 'yearly';
export type PrepaidPlanId = '3m' | '6m' | '1y' | '5y';
export type FreePlanId = 'free';
export type PlanId = FreePlanId | RecurringPlanId | PrepaidPlanId;

// Stripe price type for recurring plans
export type StripePriceType = RecurringPlanId | `prepaid_${PrepaidPlanId}`;

// Arrays for validation schemas (useful for Zod enums)
export const FREE_PLAN_IDS: FreePlanId[] = ['free'];
export const RECURRING_PLAN_IDS: RecurringPlanId[] = ['monthly', 'yearly'];
export const PREPAID_PLAN_IDS: PrepaidPlanId[] = ['3m', '6m', '1y', '5y'];
export const ALL_PLAN_IDS: PlanId[] = [...FREE_PLAN_IDS, ...RECURRING_PLAN_IDS, ...PREPAID_PLAN_IDS];
export const STRIPE_PRICE_TYPES: StripePriceType[] = [
  ...RECURRING_PLAN_IDS,
  ...PREPAID_PLAN_IDS.map(id => `prepaid_${id}` as const)
];

// Price limits and business logic
export const PRICING_LIMITS = {
  // Maximum number of items for free tier
  FREE_TIER_MAX_ITEMS: 1000000, // Effectively unlimited for local storage
  
  // Maximum number of vaults for free tier
  FREE_TIER_MAX_VAULTS: 1000000, // Effectively unlimited for local storage
  
  // Cloud sync limits for premium tiers
  PREMIUM_MAX_CLOUD_VAULTS: 100,
  PREMIUM_MAX_ITEMS_PER_VAULT: 10000,
  
  // File size limits
  MAX_THUMBNAIL_SIZE_BYTES: 100 * 1024, // 100KB
  MAX_NOTE_SIZE_BYTES: 1024 * 1024, // 1MB
} as const;